import { CompanyPensionProduct } from "../../../types/CompanyPensionProduct";
import useApplicationStore from "../../../state/ApplicationStore";
import DynamicText from "../../common/DynamicText";
import FeeDetailsTable from "../../common/fee-details/FeeDetailsTable";
import Segment from "../../common/Segment";
import { Button, useMatomo } from "frno-react";
import useDataStore from "../../../state/DataStore";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import { TablePath, TableType } from "../../../data/TableType";
import { useEffect, useRef } from "react";

type ExpandedCompanyRowProps = {
  companyPensionProduct: CompanyPensionProduct;
};

export default function ExpandedCompanyRow({
  companyPensionProduct,
}: ExpandedCompanyRowProps) {
  const { savingsValue, setSelectedCompany, updateAutoScroll } =
    useApplicationStore();
  const {
    selectableCompanies: {
      [TableType.SAVINGSPROFILES]: companiesWithSavingsProfile,
    },
  } = useDataStore();

  const navigate = useNavigate();
  const { productType } = useParams();
  const { trackEvent } = useMatomo();
  const hasTrackedEvent = useRef(false); //Mitigates calling the matomo function twice

  function navigateToSavingsProfiles(companyName: string) {
    setSelectedCompany(companyName);
    navigate(`/${productType}${TablePath[TableType.SAVINGSPROFILES]}`);
  }

  useEffect(() => {
    if (!hasTrackedEvent.current) {
      trackEvent({
        category: "Pensjon",
        action: `Pensjon: ${productType} - Utvidelse av selskap`,
        name: `${companyPensionProduct.company.name}`,
      });
      hasTrackedEvent.current = true;
    }
  }, [trackEvent, companyPensionProduct.company.name]);

  return (
    <>
      <h4>
        <DynamicText
          textKey="page.companies.table.expandedrow.header"
          replaceComponent={
            companyPensionProduct.nameOfLegalPersonOfferingTheProduct
          }
        />
      </h4>

      <FeeDetailsTable
        headerKey="page.companies.table.expandedrow.feedetails.header"
        computedFees={companyPensionProduct.computedFees}
        savingsValue={savingsValue}
      />

      {companyPensionProduct.fees.feeDescription && (
        <Segment
          headerKey="page.companies.table.expandedrow.feedescription.header"
          mainContent={companyPensionProduct.fees.feeDescription}
        />
      )}

      {companyPensionProduct.setupFeeDescription && (
        <Segment
          headerKey="page.companies.table.expandedrow.setupfeedescription.header"
          mainContent={companyPensionProduct.setupFeeDescription}
        />
      )}

      {companyPensionProduct.productDetails && (
        <Segment
          headerKey="page.companies.table.expandedrow.productdetails.header"
          mainContent={companyPensionProduct.productDetails}
        />
      )}

      <div className="flex flex-wrap gap-5 mt-8">
        {companiesWithSavingsProfile?.has(
          companyPensionProduct.company.name,
        ) && (
          <Button
            onClick={() => {
              updateAutoScroll(true);
              navigateToSavingsProfiles(companyPensionProduct.company.name);
              trackEvent({
                category: `Pensjon`,
                action: `Pensjon: ${productType} - Knapp "Se spareprofiler"`,
                name: `${companyPensionProduct.company.name}`,
              });
            }}
            className="btn-featured"
            icon="fa-chevron-right"
          >
            <DynamicText textKey="page.companies.table.expandedrow.button.savingsprofiles" />
          </Button>
        )}
        <Button
          onClick={() => {
            trackEvent({
              category: `Pensjon`,
              action: `Pensjon: ${productType} - Knapp "Gå til selskap"`,
              name: `${companyPensionProduct.company.name}`,
            });
          }}
          href={companyPensionProduct.url}
          className="btn-featured"
          icon="fa-arrow-up-right-from-square"
          target="_blank"
        >
          <DynamicText
            textKey="page.companies.table.expandedrow.button.company"
            replaceComponent={companyPensionProduct.company.name}
          />
        </Button>
        {companyPensionProduct.urlOfCheapestGlobalMutualFundOfferedInTheProduct && (
          <Button
            onClick={() => {
              trackEvent({
                category: `Pensjon`,
                action: `Pensjon: ${productType} - Knapp "Gå til fond"`,
                name: `${companyPensionProduct.company.name}`,
              });
            }}
            href={
              companyPensionProduct.urlOfCheapestGlobalMutualFundOfferedInTheProduct
            }
            className="btn-featured"
            icon="fa-arrow-up-right-from-square"
            target="_blank"
          >
            <DynamicText
              textKey="page.companies.table.expandedrow.button.company"
              replaceComponent={companyPensionProduct.name}
            />
          </Button>
        )}
      </div>
    </>
  );
}
